import { addClass, removeClass } from '../util.js';

/**
 * Profile Tab Switching
 * ES5-compatible JavaScript for handling tab navigation with hash-based routing
 *
 * Features:
 * - Client-side tab switching without page reloads
 * - URL hash support for deep-linking (e.g., /profile/username#security)
 * - Browser back/forward button support
 * - Graceful degradation (all content visible if JS disabled)
 */

(function() {
    'use strict';

    // Cache DOM elements
    var tabLinks = null;
    var tabPanels = null;
    var initialized = false;

    /**
     * Initialize tab functionality
     */
    function init() {
        if (initialized) return;

        // Get all tab links and panels
        tabLinks = document.querySelectorAll('.tab-link');
        tabPanels = document.querySelectorAll('.tab-panel');

        if (!tabLinks.length || !tabPanels.length) {
            return; // No tabs on this page
        }

        // Attach click event listeners to tab links
        for (var i = 0; i < tabLinks.length; i++) {
            tabLinks[i].addEventListener('click', handleTabClick);
        }

        // Listen for hash changes (browser back/forward)
        if (window.addEventListener) {
            window.addEventListener('hashchange', handleHashChange);
        } else if (window.attachEvent) {
            // IE8 fallback
            window.attachEvent('onhashchange', handleHashChange);
        }

        // Initialize correct tab based on URL hash
        var hash = window.location.hash.slice(1); // Remove the # symbol
        if (hash) {
            switchTab(hash);
        } else {
            switchTab('profile'); // Default to profile tab
        }

        initialized = true;
    }

    /**
     * Handle tab link clicks
     * @param {Event} event - Click event
     */
    function handleTabClick(event) {
        event.preventDefault();
        var tabId = this.getAttribute('data-tab');

        if (tabId) {
            // Update URL hash without triggering page scroll
            if (history.pushState) {
                history.pushState(null, null, '#' + tabId);
            } else {
                // Fallback for older browsers
                window.location.hash = tabId;
            }

            switchTab(tabId);
        }
    }

    /**
     * Handle hash change events (browser navigation)
     */
    function handleHashChange() {
        var hash = window.location.hash.slice(1) || 'profile';
        switchTab(hash);
    }

    /**
     * Switch to the specified tab
     * @param {string} tabId - The ID of the tab to switch to
     */
    function switchTab(tabId) {
        if (!tabId) return;

        // Sanitize tabId to prevent XSS
        tabId = tabId.replace(/[^a-z0-9-_]/gi, '');

        // Hide all tab panels
        for (var i = 0; i < tabPanels.length; i++) {
            removeClass(tabPanels[i], 'active');
            tabPanels[i].style.display = 'none';
        }

        // Show the target tab panel
        var targetPanel = document.querySelector('[data-tab-panel="' + tabId + '"]');
        if (targetPanel) {
            addClass(targetPanel, 'active');
            targetPanel.style.display = 'block';
        }

        // Update tab link active states
        updateTabLinkStates(tabId);

        // Scroll to top of content (optional, for better UX)
        if (targetPanel) {
            var mainContent = targetPanel.parentElement;
            if (mainContent) {
                var scrollTop = mainContent.offsetTop - 100; // Account for sticky header
                window.scrollTo({
                    top: scrollTop > 0 ? scrollTop : 0,
                    behavior: 'smooth'
                });
            }
        }
    }

    /**
     * Update active/inactive states on tab links
     * @param {string} activeTabId - The ID of the currently active tab
     */
    function updateTabLinkStates(activeTabId) {
        for (var i = 0; i < tabLinks.length; i++) {
            var link = tabLinks[i];
            var tabId = link.getAttribute('data-tab');

            if (tabId === activeTabId) {
                // Activate this tab
                removeClass(link, 'tab-inactive');
                addClass(link, 'tab-active');

                // Update icon color for active tab
                var icon = link.querySelector('.material-symbols-outlined');
                if (icon) {
                    addClass(icon, 'text-primary');
                }
            } else {
                // Deactivate this tab
                removeClass(link, 'tab-active');
                addClass(link, 'tab-inactive');

                // Remove icon color for inactive tabs
                var icon = link.querySelector('.material-symbols-outlined');
                if (icon) {
                    removeClass(icon, 'text-primary');
                }
            }
        }
    }

    // classList polyfill removed — using centralized DOMUtils from ../util.js

    // Initialize when DOM is ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        // DOM already loaded
        init();
    }

})();
